import { useQuery } from '@tanstack/react-query';


import { apiClient } from '../apiClient';
import { queryKeys } from '../queryKeys';

import { useProblemSubmissions } from './useProblemSubmissions';

import { useAuthStore } from '@/stores/authStore';

export interface EditorialAccess {
  unlocked: boolean;
  /** 'solved' | 'released' | 'staff' | 'locked' */
  reason: string;
}

/**
 * Asks the server whether this caller may read the editorial.
 *
 * The client used to compute this by itself, and could: the two unlocks that
 * existed were a staff role and an accepted verdict, both of which the browser
 * already knows. A trainer releasing the topic is the third, and it is a fact
 * only the server holds — no amount of local state can derive it.
 *
 * Returns the ANSWER, never the editorial, so asking costs nothing even when
 * the answer is no.
 */
function useEditorialAccessQuery(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.problems.editorialAccess(slug),
    queryFn: async () => {
      const { data } = await apiClient.get<EditorialAccess>(
        `/api/v1/problems/${slug}/editorial/access`,
      );
      return data;
    },
    enabled: !!slug && enabled,
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Whether the editorial should be offered to this caller.
 *
 * A UI convenience, not the enforcement. The enforcement is
 * problems.Service.GetEditorial, which re-derives all three unlocks from the
 * database and refuses a caller who never asked this question or asked it
 * about a different problem.
 *
 * Four sources, because no single one covers every moment:
 *  - `solvedNow`: the submission that was just accepted in this session. The
 *    submissions list has not necessarily refetched yet, and making a student
 *    reload to see the editorial they just earned would be absurd.
 *  - a prior accepted submission, which is what survives a page reload.
 *  - a staff role, which the server honours without any submission at all.
 *  - the server's answer, which is the only one that can see a trainer's
 *    release — and the only one that is authoritative about any of it.
 *
 * The three local checks are kept in front of the server's, and are not
 * merely an optimisation: they are what makes the tab appear the instant a
 * solve lands rather than one round-trip later. They can only ever ADD to what
 * the server says, and each one mirrors a branch the server also applies, so a
 * local yes is never a lie the server would contradict.
 *
 * The query is skipped for a signed-out visitor. The route is authenticated,
 * so asking would only put a 401 in their network tab.
 */
export function useEditorialAccess(slug: string, solvedNow: boolean): boolean {
  const isStaff = useAuthStore((s) => s.hasRole('trainer', 'org_admin', 'super_admin'));
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: submissions } = useProblemSubmissions(slug);
  const solvedBefore = (submissions ?? []).some((s) => s.verdict === 'AC');

  const locallyUnlocked = isStaff || solvedNow || solvedBefore;
  const { data: access } = useEditorialAccessQuery(slug, isAuthenticated);

  return locallyUnlocked || access?.unlocked === true;
}
